import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ttsApi } from '../lib/api';
import { Volume2, VolumeX, X, Play, GraduationCap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getAvatarById } from './avatarDefinitions';

const LANG_MAP = {
  en: 'English', hi: 'Hindi', ta: 'Tamil', te: 'Telugu', kn: 'Kannada',
  ml: 'Malayalam', bn: 'Bengali', mr: 'Marathi', gu: 'Gujarati',
  pa: 'Punjabi', or: 'Odia', as: 'Assamese',
};

export function TeacherAvatar({ text, cacheKey, onClose, visible = true, avatarId = 'male-1', dismissed = false, onResummon }) {
  const [phase, setPhase] = useState('idle');
  const [muted, setMuted] = useState(false);
  const [narrationText, setNarrationText] = useState('');
  const [audioReady, setAudioReady] = useState(false);
  const [mouthOpen, setMouthOpen] = useState(false);
  const audioRef = useRef(null);
  const fadeTimer = useRef(null);
  const mouthTimer = useRef(null);
  const activeRequestId = useRef(0);

  const avatar = getAvatarById(avatarId);

  // Cleanup helper
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onplay = null;
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current = null;
    }
    clearInterval(mouthTimer.current);
    clearTimeout(fadeTimer.current);
    setMouthOpen(false);
  }, []);

  // SINGLE effect: handles reset + fetch whenever visible/text/cacheKey changes
  useEffect(() => {
    if (!visible || !text) return;

    // Bump request ID to cancel stale responses
    const requestId = ++activeRequestId.current;

    // Stop any currently playing audio
    stopAudio();
    setPhase('loading');
    setNarrationText('');
    setAudioReady(false);

    const fetchAndPlay = async () => {
      try {
        const lang = localStorage.getItem('preferredLanguage') || 'en';
        const langName = LANG_MAP[lang] || 'English';
        const resp = await ttsApi.generate(text, cacheKey || '', lang, langName);

        // Abort if a newer request has been made
        if (requestId !== activeRequestId.current) return;

        setNarrationText(resp.narration_text || text);

        if (resp.audio_base64) {
          const audio = new Audio(`data:audio/mp3;base64,${resp.audio_base64}`);
          audioRef.current = audio;
          audio.volume = muted ? 0 : 0.8;
          audio.onplay = () => {
            if (requestId !== activeRequestId.current) { audio.pause(); return; }
            setPhase('speaking');
            mouthTimer.current = setInterval(() => setMouthOpen(v => !v), 130);
          };
          audio.onended = () => {
            if (requestId !== activeRequestId.current) return;
            setPhase('done');
            clearInterval(mouthTimer.current);
            setMouthOpen(false);
            fadeTimer.current = setTimeout(() => {
              if (requestId === activeRequestId.current) onClose?.();
            }, 3000);
          };
          audio.onerror = () => {
            if (requestId !== activeRequestId.current) return;
            setPhase('done');
            setMouthOpen(false);
          };
          setAudioReady(true);
          setPhase('ready');
          try { await audio.play(); } catch { setPhase('ready'); }
        } else {
          setPhase('done');
          fadeTimer.current = setTimeout(() => {
            if (requestId === activeRequestId.current) onClose?.();
          }, 6000);
        }
      } catch {
        if (requestId !== activeRequestId.current) return;
        setNarrationText(text);
        setPhase('done');
        fadeTimer.current = setTimeout(() => {
          if (requestId === activeRequestId.current) onClose?.();
        }, 6000);
      }
    };

    fetchAndPlay();

    return () => {
      // Cleanup on unmount or before re-run
      stopAudio();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, text, cacheKey]);

  const handleClose = (e) => {
    e.stopPropagation();
    e.preventDefault();
    activeRequestId.current++;
    stopAudio();
    setPhase('idle');
    onClose?.();
  };

  const handlePlay = async (e) => {
    e.stopPropagation();
    if (audioRef.current) {
      try { await audioRef.current.play(); } catch { setPhase('done'); }
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setMuted(m => {
      if (audioRef.current) audioRef.current.volume = m ? 0.8 : 0;
      return !m;
    });
  };

  const isSpeaking = phase === 'speaking';
  const displayText = narrationText || text;
  const showBubble = phase !== 'idle';

  return (
    <>
      {/* Full Avatar */}
      <AnimatePresence>
        {visible && (
          <motion.div
            drag
            dragMomentum={false}
            dragElastic={0.1}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="fixed bottom-4 right-4 z-[9999] flex items-end gap-0"
            style={{ touchAction: 'none' }}
            data-testid="teacher-avatar-container"
          >
            {/* Speech Bubble (left of avatar) */}
            <AnimatePresence>
              {showBubble && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.85, x: 20 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  transition={{ duration: 0.3 }}
                  className="mb-20 max-w-[300px] relative"
                  data-testid="teacher-speech-bubble"
                >
                  <div className="absolute right-[-8px] bottom-6 w-0 h-0"
                    style={{ borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '10px solid rgba(15,23,42,0.95)' }} />
                  <div className="px-4 py-3 rounded-2xl bg-slate-900/95 backdrop-blur-md border border-slate-700/60 shadow-2xl">
                    {phase === 'loading' ? (
                      <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <span className="inline-flex gap-0.5">
                          {[0, 1, 2].map(i => (
                            <motion.span key={i} animate={{ opacity: [0.3, 1, 0.3] }}
                              transition={{ repeat: Infinity, delay: i * 0.2, duration: 0.8 }}
                              className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />
                          ))}
                        </span>
                        {avatar.name} is preparing...
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-slate-200 leading-relaxed">
                          {displayText?.slice(0, 300)}{displayText?.length > 300 ? '...' : ''}
                        </p>
                        {phase === 'ready' && audioReady && (
                          <button
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={handlePlay}
                            className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium transition-colors"
                            data-testid="avatar-play-btn">
                            <Play className="w-3 h-3" fill="white" /> Listen
                          </button>
                        )}
                        {phase === 'speaking' && (
                          <div className="mt-2 flex items-center gap-1 text-blue-400 text-xs">
                            {[0, 1, 2, 3, 4].map(i => (
                              <motion.div key={i}
                                animate={{ scaleY: [0.4, 1, 0.4] }}
                                transition={{ repeat: Infinity, delay: i * 0.1, duration: 0.5 }}
                                className="w-1 h-3 bg-blue-400 rounded-full" />
                            ))}
                            <span className="ml-1">Speaking...</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Avatar Image */}
            <div className="relative flex-shrink-0" style={{ width: 160, height: 220 }}>
              <img
                src={avatar.image}
                alt={avatar.name}
                className="w-full h-full object-contain object-bottom pointer-events-none select-none"
                style={{ background: 'transparent' }}
                data-testid="teacher-avatar-image"
                draggable={false}
              />
              {/* Lip movement overlay */}
              {isSpeaking && (
                <div className="absolute pointer-events-none" style={{ bottom: '55%', left: '50%', transform: 'translateX(-50%)' }}>
                  <motion.div
                    animate={{ scaleY: mouthOpen ? 1.3 : 0.4, scaleX: mouthOpen ? 1 : 0.8 }}
                    transition={{ duration: 0.12 }}
                    className="w-5 h-3 rounded-full bg-red-800/40"
                  />
                </div>
              )}
              {/* Controls - use onPointerDown stopPropagation to prevent drag from eating clicks */}
              <div className="absolute top-1 right-1 flex gap-1.5">
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={toggleMute}
                  className="w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center hover:bg-black/80 transition-colors"
                  data-testid="avatar-mute-btn">
                  {muted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-white" />}
                </button>
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={handleClose}
                  className="w-8 h-8 rounded-full bg-red-600/80 backdrop-blur-sm flex items-center justify-center hover:bg-red-500 transition-colors"
                  data-testid="avatar-close-btn">
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Resummon Button */}
      <AnimatePresence>
        {!visible && dismissed && onResummon && (
          <motion.button
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            onClick={onResummon}
            className="fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full bg-primary shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center group"
            title="Show Teacher Avatar"
            data-testid="avatar-resummon-btn"
          >
            <GraduationCap className="w-6 h-6 text-white" />
            <span className="absolute -top-8 right-0 text-xs bg-slate-900 text-white px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Show Teacher
            </span>
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
